// services/api.js - API 服务层
const BASE = '/api';

const getToken = () => localStorage.getItem('sessionToken');

const request = async (path, options = {}) => {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  // 401 说明 token 无效/过期，清除本地状态并跳转登录
  if (res.status === 401) {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('loginIp');
    window.location.href = '/';
    return { success: false, message: 'Unauthorized' };
  }

  // 403 说明权限不足，抛出错误由调用方处理
  if (res.status === 403) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || '权限不足');
    err.isAuthError = true;
    throw err;
  }

  try {
    return await res.json();
  } catch {
    return { success: false, message: 'Invalid response' };
  }
};

// 认证
export const api = {
  login: (data) => request('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  logout: (data) => {
    const token = getToken();
    return request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ ...data, token })
    });
  },
  getUserInfo: (id) => request(`/auth/user/info?id=${id}`),

  // 设备
  getDeviceList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/device/list?${qs}`);
  },
  getDeviceStats: () => request('/device/stats'),
  getDevice: (id) => request(`/device/${id}`),
  addDevice: (data) => request('/device/add', { method: 'POST', body: JSON.stringify(data) }),
  updateDevice: (id, data) => request(`/device/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDevice: (id) => request(`/device/${id}`, { method: 'DELETE' }),

  // 功率数据
  getPowerRealtime: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/power/realtime?${qs}`);
  },
  getPowerLatest: () => request('/power/latest'),
  getPowerHistory: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/power/history?${qs}`);
  },
  getPowerDaily: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/power/daily?${qs}`);
  },
  getPowerOverview: () => request('/power/overview'),
  getPower24h: () => request('/power/24h'),

  // 日志
  getLogList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/log/list?${qs}`);
  },
  getLogStats: () => request('/log/stats'),
  deleteLog: (id) => request(`/log/${id}`, { method: 'DELETE' }),
  cleanLog: () => request('/log/clean/all', { method: 'DELETE' }),

  // 用户
  getUserList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/user/list?${qs}`);
  },
  addUser: (data) => request('/user/add', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/user/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/user/${id}`, { method: 'DELETE' }),
  getUserDevices: (userId) => request(`/user/${userId}/devices`),
  assignUserDevices: (userId, deviceIds) => request(`/user/${userId}/devices`, { method: 'POST', body: JSON.stringify({ device_ids: deviceIds }) }),
  removeUserDevice: (userId, deviceId) => request(`/user/${userId}/devices/${deviceId}`, { method: 'DELETE' }),

  // 工单
  getWorkOrderList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/workorder/list?${qs}`);
  },
  getWorkOrderStats: () => request('/workorder/stats'),
  getWorkOrder: (id) => request(`/workorder/${id}`),
  addWorkOrder: (data) => request('/workorder/add', { method: 'POST', body: JSON.stringify(data) }),
  updateWorkOrder: (id, data) => request(`/workorder/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // 告警管理
  getAlertList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/alert/list?${qs}`);
  },
  getAlertStats: () => request('/alert/stats'),
  updateAlert: (id, data) => request(`/alert/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAlert: (id) => request(`/alert/${id}`, { method: 'DELETE' }),

  // 系统配置
  getConfigList: () => request('/config/list'),
  getConfigGroup: (group) => request(`/config/group/${group}`),
  updateConfig: (key, value) => request(`/config/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  updateConfigBatch: (values) => request('/config/batch', { method: 'PUT', body: JSON.stringify({ values }) }),

  // ========== 云平台对接协议API ==========

  // 控制器设备信息 (device_info)
  getDeviceInfoList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/device-info/list?${qs}`);
  },
  // 用户管理时超级管理员查询所有设备（绕过设备访问过滤）
  getDeviceInfoListAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const token = localStorage.getItem('sessionToken');
    return fetch(`${BASE}/device-info/list-all?${qs}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    }).then(r => r.json());
  },
  getDeviceInfoStats: () => request('/device-info/stats/summary'),
  getDeviceInfo: (id) => request(`/device-info/${id}`),
  addDeviceInfo: (data) => request('/device-info/add', { method: 'POST', body: JSON.stringify(data) }),
  updateDeviceInfo: (id, data) => request(`/device-info/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDeviceInfo: (id) => request(`/device-info/${id}`, { method: 'DELETE' }),

  // 南向子设备 (sub_device)
  getSubDeviceList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/sub-device/list?${qs}`);
  },
  getSubDevice: (id) => request(`/sub-device/${id}`),
  addSubDevice: (data) => request('/sub-device/add', { method: 'POST', body: JSON.stringify(data) }),
  updateSubDevice: (id, data) => request(`/sub-device/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSubDevice: (id) => request(`/sub-device/${id}`, { method: 'DELETE' }),
  getSubDeviceStatsByType: () => request('/sub-device/stats/by-type'),

  // 遥测数据 (telemetry_v2 / telemetry_*)
  getTelemetryControllerList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telemetry/controller/list?${qs}`);
  },
  getTelemetrySubDeviceList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telemetry/sub-device/list?${qs}`);
  },
  getTelemetryControllerLatest: (device_info_id) => request(`/telemetry/controller/latest/${device_info_id}`),
  getTelemetrySubDeviceLatest: (global_no) => request(`/telemetry/sub-device/latest/${global_no}`),
  getTelemetrySubDeviceLatestAll: (device_info_id) => request(`/telemetry/sub-device/latest-all/${device_info_id}`),
  getTelemetryAllLatest: () => request('/telemetry/all-latest'),
  getTelemetryDashboardLatest: () => request('/telemetry/dashboard-latest'),
  getTelemetryTrend: (device_info_id, hours = 24) => request(`/telemetry/controller/trend/${device_info_id}?hours=${hours}`),
  getTelemetryBmsTrend: (global_no, hours = 24) => request(`/telemetry/bms/trend/${global_no}?hours=${hours}`),

  // 告警记录 (alarm_record)
  getAlarmRecordList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/alarm-record/list?${qs}`);
  },
  getAlarmRecordStats: () => request('/alarm-record/stats/summary'),
  getAlarmRecord: (id) => request(`/alarm-record/${id}`),
  handleAlarmRecord: (id, data) => request(`/alarm-record/${id}/handle`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAlarmRecord: (id) => request(`/alarm-record/${id}`, { method: 'DELETE' }),
  getAlarmRecordByCode: () => request('/alarm-record/stats/by-code'),
  getAlarmRecordWithDefinition: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/alert/record-with-definition?${qs}`);
  },

  // 控制配置 (telecontrol)
  getTelecontrolConfigList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/config/list?${qs}`);
  },
  updateTelecontrolConfig: (device_info_id, config_key, data) =>
    request(`/telecontrol/config/${device_info_id}/${config_key}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateTelecontrolConfigBatch: (data) => request('/telecontrol/config/batch', { method: 'PUT', body: JSON.stringify(data) }),

  // 时段策略 (schedule)
  getScheduleList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/schedule/list?${qs}`);
  },
  addSchedule: (data) => request('/telecontrol/schedule/add', { method: 'POST', body: JSON.stringify(data) }),
  updateSchedule: (id, data) => request(`/telecontrol/schedule/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSchedule: (id) => request(`/telecontrol/schedule/${id}`, { method: 'DELETE' }),
  publishSchedule: (data) => request('/telecontrol/schedule/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 控制操作日志 (control_log)
  getControlLogList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/log/list?${qs}`);
  },
  addControlLog: (data) => request('/telecontrol/log/add', { method: 'POST', body: JSON.stringify(data) }),

  // 查询设备控制响应状态
  getControlResponseStatus: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/response-status?${qs}`);
  },

  // 遥控下发 (Telecontrol)
  publishTelecontrol: (data) => request('/telecontrol/publish', { method: 'POST', body: JSON.stringify(data) }),
  getControllerDataList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/controller-data/list?${qs}`);
  },

  // 南向设备遥测查询 (SubTelemetry)
  publishSubTelemetry: (data) => request('/telecontrol/sub-telemetry/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 南向设备遥控 (SubTelecontrol)
  publishSubTelecontrol: (data) => request('/telecontrol/sub-telecontrol/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 控制器&南向设备报警同步 (AllAlarm)
  publishAllAlarm: (data) => request('/telecontrol/all-alarm/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 大屏数据推送控制 (DashboardCtrl)
  publishDashboardCtrl: (data) => request('/telecontrol/dashboard-ctrl/publish', { method: 'POST', body: JSON.stringify(data) }),

  // ========== 新协议下发接口 (EMS MQTT 协议 v1) ==========

  // 下发 TOU 策略 (新协议)
  publishScheduleV2: (data) => request('/telecontrol/schedule-v2/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发保护参数 (新协议)
  publishProtectionParams: (data) => request('/telecontrol/protection-params/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发智能调度指令 (新协议)
  publishSmartDispatch: (data) => request('/telecontrol/smart-dispatch/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发遥控点位 (新协议)
  publishTelecontrolV2: (data) => request('/telecontrol/telecontrol-v2/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发南向设备遥控 (新协议)
  publishSubTelecontrolV2: (data) => request('/telecontrol/sub-telecontrol-v2/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发大屏推送控制 (新协议)
  publishDashboardCtrlV2: (data) => request('/telecontrol/dashboard-ctrl-v2/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发通用控制命令 (新协议)
  publishControlV2: (data) => request('/telecontrol/control-v2/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 下发微网设备控制 (新协议)
  publishDeviceControl: (data) => request('/telecontrol/device-control/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 查询保护参数
  getProtectionParams: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/protection-params?${qs}`);
  },

  // 查询智能调度状态
  getSmartDispatchStatus: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/smart-dispatch/status?${qs}`);
  },

  // 设备配置下发
  publishDeviceConfig: (data) => request('/telecontrol/device-config/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 获取设备配置列表
  getDeviceConfig: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/device-config?${qs}`);
  },

  // 通信配置下发
  publishCommunicationConfig: (data) => request('/telecontrol/communication-config/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 获取通信配置列表
  getCommunicationConfig: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/communication-config?${qs}`);
  },

  // get_device_info 下发
  publishGetDeviceInfo: (data) => request('/telecontrol/get-device-info/publish', { method: 'POST', body: JSON.stringify(data) }),

  // get_telemetry 下发
  publishGetTelemetry: (data) => request('/telecontrol/get-telemetry/publish', { method: 'POST', body: JSON.stringify(data) }),

  // get_alarm 下发
  publishGetAlarm: (data) => request('/telecontrol/get-alarm/publish', { method: 'POST', body: JSON.stringify(data) }),

  // 查询新协议命令响应状态
  getCommandAckStatus: (requestId) => request(`/telecontrol/command_ack/status?request_id=${requestId}`),

  // 控制配置操作日志
  addControlConfigLog: (data) => request('/telecontrol/control-config-log/add', { method: 'POST', body: JSON.stringify(data) }),
  addLogConfig: (data) => request('/telecontrol/log-config/add', { method: 'POST', body: JSON.stringify(data) }),
  getControlConfigLogList: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/telecontrol/control-config-log/list?${qs}`);
  },

  // ========== 大屏仪表盘 API ==========
  getDashboardOverview: () => request('/dashboard/overview'),
  getDashboardLoadCurve: () => request('/dashboard/load-curve'),
  getDashboardStrategy: () => request('/dashboard/strategy'),
  getDashboardEnergyStats: () => request('/dashboard/energy-stats'),
};
